/**
 * The artboards about to be imported, in the rail beside the settings.
 *
 * pdf-to-figma has always shown this: a scrolling column of numbered thumbnails, so the
 * question "how should we bring it in?" is asked next to the thing it is about. This app
 * could not, because nothing knew the artboard count until the converter reported it —
 * which happens after the user has already pressed Import. `probeDocument` reads the shape
 * first, and this draws it.
 *
 * An .ai or .eps with one artboard shows one thumbnail, which is the common case and looks
 * like photoshop's single composite. A multi-artboard file scrolls.
 */

import { Box, Flex, Text } from '@figma-importers/ui';
import { useEffect, useMemo } from 'react';
import { parseRange, useImportStore } from '../store/importStore';

/**
 * Object URLs for the thumbnails, revoked together when they change or the rail unmounts.
 *
 * A memo to create and an effect only to revoke: the URLs are a pure function of the bytes,
 * and creating them in an effect would render once without images and again with them.
 */
function useObjectUrls(thumbnails: readonly Uint8Array[]): string[] {
  const urls = useMemo(
    () =>
      thumbnails.map((bytes) =>
        URL.createObjectURL(new Blob([bytes as BlobPart], { type: 'image/png' })),
      ),
    [thumbnails],
  );
  useEffect(
    () => () => {
      // Without this, every file picked leaks one blob per artboard for the life of the window.
      for (const url of urls) URL.revokeObjectURL(url);
    },
    [urls],
  );
  return urls;
}

export function ArtboardPreview() {
  const probe = useImportStore((s) => s.probe);
  const selection = useImportStore((s) => s.selection);
  const range = useImportStore((s) => s.range);
  const urls = useObjectUrls(probe?.thumbnails ?? []);

  /*
   * Which artboards the current choice would actually bring in. The rail answers "which
   * pages?" visually, so picking "First artboard" has to dim the other two — otherwise the
   * radio says one thing and the thumbnails show another.
   */
  const count = probe?.pageCount ?? 0;
  const chosen =
    selection === 'first'
      ? new Set([0])
      : selection === 'range'
        ? new Set(parseRange(range, count))
        : null; // 'all' — everything, so nothing is dimmed

  // No probe yet, or one that failed: the rail stays empty rather than showing a spinner for
  // something optional. The settings beside it are usable either way.
  if (!probe || urls.length === 0) return null;

  return (
    <Flex direction="column" gap="2">
      {urls.map((url, i) => {
        const picked = chosen === null || chosen.has(i);
        return (
        <Box
          key={url}
          position="relative"
          opacity={picked ? 1 : 0.45}
          transition="opacity 0.15s"
        >
          {/* A plain `img`: the kit's Box does not take `src`/`alt` in these types, and
              photoshop's preview settled on the same thing for the same reason. */}
          <img
            src={url}
            alt={`Artboard ${i + 1}`}
            style={{
              width: '100%',
              display: 'block',
              borderRadius: '2px',
              border: picked
                ? '1px solid var(--color-figma-accent)'
                : '1px solid var(--color-figma-border)',
              background: '#fff',
            }}
          />
          {probe.pageCount > 1 && (
            <Text
              position="absolute"
              bottom="0"
              right="0"
              px="1"
              fontSize="2xs"
              fontWeight="600"
              color="white"
              // pdf's rail: the chosen ones carry an accent chip, the rest a muted one, so
              // the numbers read as a selection rather than as decoration.
              bg={picked ? 'figma.accent' : 'figma.panel'}
              borderTopLeftRadius="sm"
              borderBottomRightRadius="sm"
            >
              {i + 1}
            </Text>
          )}
        </Box>
        );
      })}
    </Flex>
  );
}
